// src/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';

interface User {
    pk: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_staff: boolean;
    is_superuser: boolean;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (access_token: string) => Promise<void>;
    logout: () => void;
    isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export const AuthProvider: React.FC = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchUser = async () => {
        const token = Cookies.get('my-app-auth');
        if (token) {
            try {
                const res = await axios.get(`${API_BASE}/api/dj-rest-auth/user/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUser(res.data);
            } catch (err) {
                console.error("Failed to fetch user", err);
                logout();
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUser();
    }, []);

    const login = async (access_token: string) => {
        try {
            const res = await axios.post(`${API_BASE}/api/dj-rest-auth/google/`, {
                access_token: access_token,
                id_token: access_token
            });
            const { access, user } = res.data;
            Cookies.set('my-app-auth', access, { expires: 7 });
            setUser(user);
        } catch (err) {
            console.error("Login failed", err);
            throw err;
        }
    };

    const logout = () => {
        Cookies.remove('my-app-auth');
        setUser(null);
    };

    const isAdmin = user ? (user.is_staff || user.is_superuser) : false;

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, isAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
